/* eslint-disable react/display-name */
import { format } from 'date-fns';
import axios from 'helpers/axios';
import { useCancelToken, useMutableRef, useOpenState, useToasts, useUser } from 'hooks';
import React from 'react';
import { AdminLayout } from 'ui/components/layouts';
import { DataTable, DataTableCell, Drawer, IconButton } from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import { Image, Modal, ModalPosition, SuccessModal, Title } from 'ui/components/styled';
import { EditUser, InviteUser } from './forms';
import { CompaniesList } from './lists';
import { ConfirmRemoveUser } from './modals';

export const Users = () => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [perPage, setPerPage] = React.useState(15);
  const [sort, setSort] = React.useState({ column: '', order: '' });
  const [search, setSearch] = React.useState('');
  const [modal, openModal, closeModal] = useOpenState();
  const [drawer, openDrawer, closeDrawer] = useOpenState();
  const { errorToast, removeAllToasts } = useToasts();
  const { user } = useUser();
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
  const columns = [
    {
      name: 'Name',
      selector: 'name',
      cell: (row) => <DataTableCell>{row.name.full}</DataTableCell>,
      sortable: true,
    },
    {
      name: 'Role',
      selector: 'role',
      sortable: true,
      cell: (row) => {
        const isAdmin = row.roles.includes('admin');

        return (
          <DataTableCell>
            {isAdmin ? (
              <>
                super admin
                <Image width="9px" mx="xxs" src="/assets/img/icons/star.svg" alt="star" />
              </>
            ) : (
              'user'
            )}
          </DataTableCell>
        );
      },
      style: {
        textTransform: 'capitalize',
      },
    },
    {
      name: 'Email',
      selector: 'email',
      sortable: true,
      ignoreRowClick: true,
      style: { cursor: 'initial' },
    },
    {
      name: 'Phone',
      selector: 'phone',
      sortable: true,
      ignoreRowClick: true,
      style: { cursor: 'initial' },
    },
    {
      name: 'Status',
      selector: 'validations.validated',
      cell: (row) => {
        const validated = row.validations.reduce((acc, cur) => acc && cur.validated, true);
        let status, color;
        switch (validated) {
          case true:
            status = 'Validated';
            color = '#00AA22';
            break;
          case false:
          default:
            status = 'Validation sent';
            color = '#FCB14A';
            break;
        }

        return (
          status && (
            <DataTableCell title={status} color={color} bold>
              {status}
            </DataTableCell>
          )
        );
      },
      sortable: true,
    },
    {
      name: 'Last login',
      selector: 'lastLogin',
      cell: (row) => {
        let lastLoginDate;

        if (row.lastLogin?.toLowerCase()?.includes('never')) {
          lastLoginDate = row.lastLogin;
          return lastLoginDate;
        }

        return format(new Date(row.lastLogin), 'MM/dd/yyyy hh:mm:ss a');
      },
    },
    {
      name: 'Action',
      right: true,
      button: true,
      ignoreRowClick: true,
      cell: (row) => (
        <>
          <IconButton
            variant="transparent"
            icon="edit"
            mx="xxs"
            onClick={() => openModal('edit-user', { user: row })}
          />
          {user.id !== row.id && (
            <IconButton
              variant="transparent"
              icon="trash"
              mx="xxs"
              onClick={() => openModal('confirm-remove-user', { user: row })}
            />
          )}
        </>
      ),
    },
  ];

  const fetchUsers = async (page) => {
    setLoading(true);

    try {
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const response = await axios.get(
        `/uext/users/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Users.' });
    }

    setLoading(false);
  };

  const handleRowsPerPageChange = async (newPerPage, page) => {
    setLoading(true);
    setCurrentPage(page);

    try {
      const skip = page > 1 ? (page - 1) * newPerPage : 0;
      const response = await axios.get(
        `/uext/users/?$top=${newPerPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setPerPage(newPerPage);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Rows Change.' });
    }

    setLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page);
  };

  const handleSort = async (column, sortDirection) => {
    setLoading(true);

    const sortColumn = column?.selector || '';
    const sortOrder = sortColumn ? sortDirection : '';
    setSort({ column: sortColumn, order: sortOrder });

    try {
      const skip = 0;
      const response = await axios.get(
        `/uext/users/?$top=${perPage}&$skip=${skip}&$orderby=${sortColumn}&$order=${sortOrder}&$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Sort Users.' });
    }

    setLoading(false);
  };

  const handleFilter = async (value) => {
    setLoading(true);
    setSearch(value);

    cancelPreviousRequest();

    try {
      const skip = 0;
      const response = await axios.get(
        `/uext/users/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${value}`,
        { cancelToken: newCancelToken() }
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: 'Error: Filter Users.' });
    }

    setLoading(false);
  };

  const refresh = (newRowsCount) => {
    if (newRowsCount > 0) {
      const updatedPage = getNumberOfPages(newRowsCount, perPage);
      const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
      if (recalculatedPage !== currentPage.current) {
        setCurrentPage(recalculatedPage);
      }
    }

    fetchUsers(currentPage.current);
  };

  const removeUser = async (user) => {
    try {
      const response = await axios.delete(`/uext/users/${user.id}`);

      if (response.status === 204) {
        removeAllToasts();
        refresh(totalRows - 1);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove User.' });
    }

    closeModal();
  };

  const handleRowClick = (row) => {
    openDrawer(true, { user: row });
  };

  React.useEffect(() => {
    fetchUsers(1);
  }, []);

  return (
    <AdminLayout>
      <Title variant="dark" mx="m" mb="l">
        User management
      </Title>
      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        paginationPerPage={perPage}
        paginationTotalRows={totalRows}
        paginationCurrentPage={currentPage.current}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
        onPageReset={setCurrentPage}
        onSort={handleSort}
        filterPlaceholder="Users Search"
        onFilter={handleFilter}
        onRowClicked={handleRowClick}
        subHeaderActions={<IconButton icon="plus" onClick={() => openModal('invite-user')} />}
        pointerOnHover
      />
      <Modal
        show={modal.isOpen('invite-user', 'edit-user')}
        onClose={closeModal}
        position={ModalPosition.TOP_CENTER}
        top={modal.isOpen('edit-user') ? '15%' : '33%'}
        width="40%"
        maxWidth="650px"
      >
        {modal.isOpen('invite-user') && (
          <InviteUser
            onClose={closeModal}
            onSuccess={(email) => {
              refresh();
              openModal('invite-user-success', { email });
            }}
          />
        )}
        {modal.isOpen('edit-user') && (
          <EditUser user={modal.params?.user} onClose={closeModal} onSuccess={refresh} />
        )}
      </Modal>
      <ConfirmRemoveUser
        show={modal.isOpen('confirm-remove-user')}
        onClose={closeModal}
        onConfirm={() => removeUser(modal.params?.user)}
        params={modal.params}
        confirmed={false}
      />
      <SuccessModal show={modal.isOpen('invite-user-success')} onClose={closeModal}>
        Invitation successfully sent to {modal.params?.email} !
      </SuccessModal>
      <Drawer show={drawer.isOpen()} onClose={closeDrawer} width="60%">
        {drawer.params?.user && <CompaniesList user={drawer.params?.user} />}
      </Drawer>
    </AdminLayout>
  );
};
